import { useEffect, useMemo, useRef } from 'react';
import { useResizeObserver } from '../../../hooks/useResizeObserver.ts';
import { drawNoDataMessage, getGraphSvg } from '../../../utils/d3Utils.ts';
import { trpc } from '../../../utils/trpc/trpc.ts';
import { drawReportGraph } from '../drawReportGraph.ts';
import { ReportSettings } from '../report-settings/reportSettings.ts';
import { ReportGraphContainer } from '../reportGraphContainer.tsx';
import { prepareSetupTotals } from './prepareSetupTotals.ts';


// Show worker setup totals across the whole selected date range.
export function SetupsGraph({ reportSettings }: { reportSettings: ReportSettings }) {
    const graphContainerRef = useRef<HTMLDivElement>(null);

    const { data: totals, isFetching } = trpc.graphsData.getSetupTotals.useQuery({
        start: reportSettings.first.start,
        end: reportSettings.last.end,
    }, { refetchOnWindowFocus: false });

    const preparedData = useMemo(() => {
        if (!totals) return undefined;
        return prepareSetupTotals(totals);
    }, [totals]);

    const dimensions = useResizeObserver(graphContainerRef);


    useEffect(() => {
        const container = graphContainerRef.current;
        if (!container || !preparedData) return;
        if (preparedData.barGroups.length === 0) {
            drawNoDataMessage(getGraphSvg(container));
            return;
        }
        drawReportGraph(container, preparedData);
    }, [preparedData, dimensions]);

    // Keep every worker name visible using the existing horizontal graph scroll.
    const longestName = Math.max(0, ...(totals ?? []).map(({ worker }) => worker.length));
    const columnWidth = Math.max(110, longestName * 24 + 20);
    const minWidth = (totals?.length ?? 0) * columnWidth + 120;

    return (
        <ReportGraphContainer
            containerRef={graphContainerRef}
            isLoading={isFetching}
            style={{ height: '100%', minWidth }}
        />
    );
}
